import { DataThresholdingOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import RouterConstants from "../../../../constants/RouterConstants";
import { ORG_SUBSCRIPTION_PLANS } from "../../../../constants/billing";
import { RouteGroupKey } from "../../../../constants/routesMapper";
import { useApiLimitStatus } from "../../../../hooks/useApiLimitStatus";
import useSecureNavigate from "../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../hooks/useStores";
import styles from "./UsageWidgetAPI.module.scss";

export const ROUTE_GROUP_COLORS: Record<RouteGroupKey, string> = {
	image_generation: "#A000F0",
	image_editing: "#C185F4",
	video_editing: "#90e197",
	tailored_generation: "#EB2C6D",
	product_shot_editing: "#75C1B2",
	ads_generation: "#109DB6",
	others: "#BDBDBD",
};

type Props = {
	groupedUsageData: { [product in RouteGroupKey]: number };
	startDate?: string;
	endDate?: string;
};
const UsageWidgetAPI = ({ groupedUsageData, startDate = "", endDate = "" }: Props) => {
	const { t } = useTranslation("dashboard", { keyPrefix: "usage.api" });
	const navigate = useSecureNavigate();
	const { authStore, accountStore, tgStore, pricingStore } = useAppStore();
	const { tgModelStore } = tgStore;
	const { isPassedFreeLimit, freeLimit } = useApiLimitStatus();
	const isPayingOrg = authStore.orgSubscription?.plan_name !== ORG_SUBSCRIPTION_PLANS.free.name;

	const usageData = Object.entries(groupedUsageData)
		.map(([key, value]) => ({
			key: key as RouteGroupKey,
			value,
			color: ROUTE_GROUP_COLORS[key as RouteGroupKey] ?? "#999999",
		}))
		.sort((a, b) => b.value - a.value);

	// If no data with values > 0, show all route groups with zero values
	const hasData = usageData.some((item) => item.value > 0);
	const displayData = hasData ? usageData.filter((item) => item.value > 0) : usageData;

	const total = usageData.reduce((sum, item) => sum + item.value, 0);

	const renderTooltip = ({ active, payload }: any) => {
		if (active && payload?.length) {
			const { key, value } = payload[0].payload;
			return (
				<Box className={styles.tooltip}>
					{t(`options.${key}`)}: {value}
				</Box>
			);
		}
		return null;
	};

	return (
		<LoadingPlaceholder
			isLoading={
				authStore.isLoadingOrgSubscriptions ||
				accountStore.isLoadingAllUsageData ||
				tgModelStore.loadingModelsList
			}
			className={styles.loading}
		>
			<Box className={styles.container}>
				<Box className={styles.chartWrapper}>
					<Box className={styles.header}>
						<Typography className={styles.total}>
							{isPassedFreeLimit ? freeLimit : total}
							{!isPayingOrg && <>/{authStore.orgUsage?.apiFreeCallsLimit ?? freeLimit}</>}
						</Typography>
						<Typography className={styles.label}>
							{t(
								`countTimeframe.${
									isPayingOrg ? "subscribed" : !isPassedFreeLimit ? "free" : "freeUtilized"
								}`,
							)}
						</Typography>
						{isPassedFreeLimit ? (
							<BriaButton
								buttonType="primaryMedium"
								className={styles.apiBtn}
								onClick={() => pricingStore.openPricingFlow()}
							>
								{t("upgrade")}
							</BriaButton>
						) : (
							<BriaButton
								buttonType="primaryMedium"
								className={styles.apiBtn}
								onClick={() => navigate(RouterConstants.AUTH_KEYS.fullPath)}
							>
								{t("apiBtn")}
							</BriaButton>
						)}
					</Box>

					{!hasData ? (
						<Box className={styles.noDataMessage}>
							<Typography variant="body2" color="text.secondary">
								{isPayingOrg
									? t("noData", {
											startDate: startDate,
											endDate: endDate,
									  })
									: t("noDataFree")}
							</Typography>
						</Box>
					) : (
						<ResponsiveContainer maxHeight={156} aspect={1}>
							<PieChart>
								<Pie
									data={displayData}
									dataKey="value"
									nameKey="key"
									cx="50%"
									cy="50%"
									innerRadius={"40%"}
									outerRadius={"70%"}
									startAngle={-270}
									endAngle={90}
								>
									{displayData.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={entry.color} />
									))}
								</Pie>
								<Tooltip content={renderTooltip} />
							</PieChart>
						</ResponsiveContainer>
					)}
				</Box>

				<ul className={styles.legend}>
					{displayData.map(({ key, color }) => (
						<li key={key} className={styles.legendItem}>
							<span className={styles.colorSquare} style={{ backgroundColor: color }} />
							<span className={styles.legendLabel}>{t(`options.${key}`)}</span>
						</li>
					))}
				</ul>
				<BriaButton
					className={styles.seeDetails}
					buttonType="textSmall"
					onClick={() => navigate(RouterConstants.USAGE_BILLING.fullPath)}
					startIcon={<DataThresholdingOutlined />}
				>
					{t("seeDetails")}
				</BriaButton>
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(UsageWidgetAPI);
